import { useEffect, useId, useState } from 'react'
import { fetchAdminCampaigns } from '@/lib/admin'
import type { AdminCampaignListItem } from '@/types/api'
import styles from '../CustosOperacionaisPage.module.css'

type OperatingCostCampaignPickerProps = {
  excludeIds: number[]
  disabled: boolean
  onSelect: (campaign: AdminCampaignListItem) => void
}

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (error instanceof Error && (error.name === 'AbortError' || /abort/i.test(error.message))) {
    return true
  }
  return false
}

export function OperatingCostCampaignPicker({
  excludeIds,
  disabled,
  onSelect,
}: OperatingCostCampaignPickerProps) {
  const fieldId = useId()
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<AdminCampaignListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const excludeKey = excludeIds.join(',')
  const trimmedSearch = search.trim()
  const canSearch = trimmedSearch.length >= 2

  useEffect(() => {
    if (!canSearch) {
      return
    }

    const controller = new AbortController()
    const excluded = excludeKey
      .split(',')
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0)

    const timer = window.setTimeout(() => {
      setIsLoading(true)
      setError(null)
      fetchAdminCampaigns({ search: trimmedSearch, perPage: 20, signal: controller.signal })
        .then((response) => {
          if (controller.signal.aborted) return
          setResults(response.campaigns.filter((campaign) => !excluded.includes(campaign.id)))
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted || isAbortError(err)) return
          setResults([])
          setError('Não foi possível buscar as campanhas.')
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsLoading(false)
          }
        })
    }, 300)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [canSearch, trimmedSearch, excludeKey])

  const visibleResults = canSearch ? results : []

  return (
    <div className={styles.campaignPicker}>
      <label className={styles.field} htmlFor={fieldId}>
        Buscar campanha
        <input
          id={fieldId}
          className={styles.input}
          value={search}
          disabled={disabled}
          placeholder="Digite o nome da campanha"
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      {!canSearch ? (
        <p className={styles.empty}>Digite pelo menos 2 letras para buscar uma campanha.</p>
      ) : isLoading ? (
        <p className={styles.empty}>Buscando campanhas...</p>
      ) : error ? (
        <p className={styles.inlineError} role="alert">
          {error}
        </p>
      ) : visibleResults.length === 0 ? (
        <p className={styles.empty}>Nenhuma campanha encontrada.</p>
      ) : (
        <ul className={styles.campaignResultList}>
          {visibleResults.map((campaign) => (
            <li key={campaign.id}>
              <button
                type="button"
                className={styles.campaignResultButton}
                disabled={disabled}
                onClick={() => {
                  onSelect(campaign)
                  setSearch('')
                  setResults([])
                }}
              >
                <span>{campaign.name}</span>
                <span className={styles.campaignResultMeta}>
                  {campaign.institution_name ?? 'Sem entidade'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
